import { Link } from "react-router";
import { MapPin, TrendingUp } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "./ui/button";

interface EnhancedProjectCardProps {
  id: string;
  title: string;
  location: string;
  description: string;
  image: string;
  raised: number;
  goal: number;
  raisedLabel?: string;
  goalLabel?: string;
  completionLabel?: string;
  hideFundingAmounts?: boolean;
  category: string;
}

export function EnhancedProjectCard({
  id,
  title,
  location,
  description,
  image,
  raised,
  goal,
  raisedLabel,
  goalLabel,
  completionLabel,
  hideFundingAmounts,
  category,
}: EnhancedProjectCardProps) {
  const progress = goal > 0 ? (raised / goal) * 100 : 0;

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="group overflow-hidden rounded-2xl bg-white border border-border hover:border-primary/50 hover:shadow-2xl transition-all"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold">
          {category}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <MapPin size={16} />
          <span>{location}</span>
        </div>

        <h3 className="text-xl mb-3 line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>

        <p className="text-sm text-muted-foreground mb-6 line-clamp-2 leading-relaxed">
          {description}
        </p>

        {/* Animated Progress Bar */}
        <div className="space-y-4 mb-6">
          <div className="relative h-3 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${progress}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/80 rounded-full"
            />
          </div>

          <div className="flex items-end justify-between">
            <div>
              <div className="text-2xl font-bold text-primary">
                {hideFundingAmounts ? raisedLabel || "Self Funded" : `₹${(raised / 100000).toFixed(1)}L`}
              </div>
              <div className="text-xs text-muted-foreground">
                {hideFundingAmounts ? goalLabel || "Goal Achieved" : `raised of ₹${(goal / 100000).toFixed(1)}L`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold">{hideFundingAmounts ? completionLabel || "100%" : `${progress.toFixed(0)}%`}</div>
              <div className="text-xs text-muted-foreground">funded</div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        {hideFundingAmounts ? (
          <Link to={`/projects/${id}`}>
            <Button variant="outline" size="lg" className="w-full">
              Details
            </Button>
          </Link>
        ) : (
          <div className="flex gap-3">
            <Link to={`/projects/${id}`} className="flex-1">
              <Button className="w-full" size="lg">
                Donate Now
              </Button>
            </Link>
            <Link to={`/projects/${id}`}>
              <Button variant="outline" size="lg">
                Details
              </Button>
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
}
